/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useEffect, useRef} from 'react';

import {VIDEO_AUTOPILOT_BEHAVIORS} from '@amblique/base/app/constants/video-options';
import {videoPilot} from '@amblique/base/app/plugins/video/video-pilot';

/**
 * React hook for video autopilot (intersection-based playback)
 *
 * Automatically controls video playback based on visibility using IntersectionObserver.
 * Implements behaviors like autoplay, resume, and autopause.
 *
 * @param {Object} params
 * @param {HTMLElement} params.elementRef - Video container ref
 * @param {AbstractVideo} params.video - Video instance
 * @param {Object} params.options - Video options
 * @param {Function} params.init - Init function for lazy loading
 */
export function useVideoAutopilot({elementRef, video, options, init}) {
  const observerRef = useRef(null);
  const autopilotRef = useRef(null);

  /**
   * Resolve autopilot behavior configuration
   */
  useEffect(() => {
    if (!options?.autopilot) {
      autopilotRef.current = null;

      return;
    }

    const autopilot = options.autopilot;

    // Boolean: use default behavior
    if (autopilot === true) {
      autopilotRef.current = VIDEO_AUTOPILOT_BEHAVIORS.default;

      return;
    }

    // String: lookup named behavior
    if (typeof autopilot === 'string') {
      autopilotRef.current = VIDEO_AUTOPILOT_BEHAVIORS[autopilot] || VIDEO_AUTOPILOT_BEHAVIORS.default;

      return;
    }

    // Object: custom behavior
    if (typeof autopilot === 'object') {
      autopilotRef.current = {
        ...VIDEO_AUTOPILOT_BEHAVIORS.default,
        ...autopilot,
      };

      return;
    }

    autopilotRef.current = null;
  }, [video]);

  /**
   * Setup IntersectionObserver
   */
  useEffect(() => {
    const autopilot = autopilotRef.current;

    if (!autopilot || (!autopilot.enter && !autopilot.leave)) {
      return;
    }

    const threshold = autopilot.threshold ?? 0.25;
    const rootMargin = autopilot.rootMargin ?? '0px';
    const {player, initialized} = video || {player: null, initialized: false};
    let visible = false;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!visible && entry.intersectionRatio >= threshold) {
          if (autopilot.enter) {
            videoPilot.enter({player, initialized, init, behaviors: autopilot.enter});
          }
        } else if (visible && autopilot.leave) {
          videoPilot.leave({player, initialized, behaviors: autopilot.leave});
        }

        visible = entry.intersectionRatio >= threshold;
      });
    }, {threshold, rootMargin});

    observer.observe(elementRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [video, init, elementRef, autopilotRef.current]);
}
